import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { User, Lock, Mail, HelpCircle, Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  
  const [recoveryData, setRecoveryData] = useState({
    email: "",
    username: "",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/login`,
        credentials,
        { withCredentials: true }
      );
      
      toast.success("Connexion réussie");
      navigate("/dashboard", { state: { user: response.data }, replace: true });
    } catch (error) {
      console.error("Login error:", error);
      toast.error(
        error.response?.data?.detail || "Nom d'utilisateur ou mot de passe incorrect"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverUsername = async () => {
    if (!recoveryData.email) {
      toast.error("Veuillez entrer votre adresse email");
      return;
    }
    
    setRecoveryLoading(true);
    try {
      await axios.post(`${API}/auth/recover-username`, {
        email: recoveryData.email,
      });
      toast.success("Si cette adresse email est associée à un compte, vous recevrez un email avec votre nom d'utilisateur.");
      setRecoveryDialogOpen(false);
      setRecoveryData({ email: "", username: "" });
    } catch (error) {
      toast.error("Erreur lors de la récupération");
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleRecoverPassword = async () => {
    if (!recoveryData.username && !recoveryData.email) {
      toast.error("Veuillez entrer votre nom d'utilisateur ou votre email");
      return;
    }
    
    setRecoveryLoading(true);
    try {
      await axios.post(`${API}/auth/recover-password`, {
        username: recoveryData.username,
        email: recoveryData.email,
      });
      toast.success("Si les informations correspondent à un compte, vous recevrez un email avec les instructions de réinitialisation.");
      setRecoveryDialogOpen(false);
      setRecoveryData({ email: "", username: "" });
    } catch (error) {
      toast.error("Erreur lors de la récupération");
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background image with overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1761871639838-ddb5b9f307d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwb2ZmaWNlJTIwYWJzdHJhY3QlMjBnZW9tZXRyaWN8ZW58MHx8fHwxNzcwNDI1NjMyfDA&ixlib=rb-4.1.0&q=85')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-blue-900/80 backdrop-blur-sm" />

      {/* Login card */}
      <Card
        className="relative z-10 w-full max-w-md mx-4 bg-card/95 backdrop-blur-md border-border/50 shadow-2xl"
        data-testid="login-card"
      >
        <CardHeader className="text-center pb-2">
          {/* Logo and title */}
          <div className="flex items-center justify-center mx-auto mb-4">
            <img 
              src="/logo-dtmax.png" 
              alt="DT-MAX Logo" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground tracking-tight">
            Dossierya
          </CardTitle>
          <CardDescription>
            Suivi des déclarations de revenus
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleLogin} className="space-y-4">
            {/* Username field */}
            <div className="space-y-2">
              <Label htmlFor="username">Nom d'utilisateur</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="username"
                  type="text"
                  value={credentials.username}
                  onChange={(e) =>
                    setCredentials({ ...credentials, username: e.target.value })
                  }
                  className="pl-10"
                  placeholder="Entrez votre nom d'utilisateur"
                  required
                  autoComplete="username"
                  data-testid="username-input"
                />
              </div>
            </div>

            {/* Password field */}
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={credentials.password}
                  onChange={(e) =>
                    setCredentials({ ...credentials, password: e.target.value })
                  }
                  className="pl-10 pr-10"
                  placeholder="Entrez votre mot de passe"
                  required
                  autoComplete="current-password"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Login button */}
            <Button
              type="submit"
              className="w-full h-11 text-base font-medium shadow-lg hover:shadow-xl transition-all duration-200"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  Connexion...
                </>
              ) : (
                "Se connecter"
              )}
            </Button>
          </form>

          {/* Recovery link */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setRecoveryDialogOpen(true)}
              className="text-sm text-primary hover:underline flex items-center justify-center gap-1 mx-auto"
              data-testid="recovery-link"
            >
              <HelpCircle className="h-4 w-4" />
              Problème de connexion ?
            </button>
          </div>

          {/* Footer */}
          <p className="text-xs text-center text-muted-foreground mt-6">
            Accès réservé aux utilisateurs autorisés
          </p>
        </CardContent>
      </Card>

      {/* Version info */}
      <div className="absolute bottom-4 left-4 text-xs text-white/50">
        <span className="font-mono">v1.0.0</span> | Année d'imposition 2025
      </div>

      {/* Recovery Dialog */}
      <Dialog open={recoveryDialogOpen} onOpenChange={setRecoveryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Récupération de compte</DialogTitle>
            <DialogDescription>
              Choisissez le type de récupération dont vous avez besoin
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="username" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="username" data-testid="tab-recover-username">
                Nom d'utilisateur
              </TabsTrigger>
              <TabsTrigger value="password" data-testid="tab-recover-password">
                Mot de passe
              </TabsTrigger>
            </TabsList>

            <TabsContent value="username" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Entrez l'adresse email associée à votre compte pour recevoir votre nom d'utilisateur.
              </p>
              <div className="space-y-2">
                <Label htmlFor="recovery-email-username">Adresse email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="recovery-email-username"
                    type="email"
                    value={recoveryData.email}
                    onChange={(e) =>
                      setRecoveryData({ ...recoveryData, email: e.target.value })
                    }
                    className="pl-10"
                    placeholder="votre@email.com"
                    data-testid="recovery-email-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleRecoverUsername}
                disabled={recoveryLoading}
                className="w-full"
                data-testid="recover-username-button"
              >
                {recoveryLoading ? "Envoi..." : "Récupérer le nom d'utilisateur"}
              </Button>
            </TabsContent>

            <TabsContent value="password" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Entrez votre nom d'utilisateur ou email pour recevoir les instructions de réinitialisation.
              </p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="recovery-username">Nom d'utilisateur</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="recovery-username"
                      type="text"
                      value={recoveryData.username}
                      onChange={(e) =>
                        setRecoveryData({ ...recoveryData, username: e.target.value })
                      }
                      className="pl-10"
                      placeholder="Votre nom d'utilisateur"
                      data-testid="recovery-username-input"
                    />
                  </div>
                </div>
                <div className="text-center text-sm text-muted-foreground">ou</div>
                <div className="space-y-2">
                  <Label htmlFor="recovery-email-password">Adresse email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="recovery-email-password"
                      type="email"
                      value={recoveryData.email}
                      onChange={(e) =>
                        setRecoveryData({ ...recoveryData, email: e.target.value })
                      }
                      className="pl-10"
                      placeholder="votre@email.com"
                      data-testid="recovery-email-password-input"
                    />
                  </div>
                </div>
              </div>
              <Button
                onClick={handleRecoverPassword}
                disabled={recoveryLoading}
                className="w-full"
                data-testid="recover-password-button"
              >
                {recoveryLoading ? "Envoi..." : "Réinitialiser le mot de passe"}
              </Button>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setRecoveryDialogOpen(false)}
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
